// A flower-peeled mandarin. Each petal is a Verlet chain (Jakobsen, "Advanced
// Character Physics", GDC 2001) hinged where the peel still clings to the
// fruit. The hinge moves by Kendall's thin-film peel condition (1975) in its
// inextensible form, F/b = R/(1 - cos θ) (his Eq. 5): pulling the peel back
// over itself opens it easily and sliding it along the fruit does not. Real
// films stretch, which caps the force at very low angles; the toy ignores that. White pith strands stretch between peel and fruit and snap at random
// lengths, leaving short wisps on the fruit.
// Node 0 is a petal's tip; the last node is its base, which never lets go.

const identity=[1,0,0,0,1,0,0,0,1];
// Rodrigues rotation taking unit a to unit b, as a row-major 3x3 matrix.
function rotationBetween(ax,ay,az,bx,by,bz,out,o=0){
  const vx=ay*bz-az*by,vy=az*bx-ax*bz,vz=ax*by-ay*bx,c=ax*bx+ay*by+az*bz;
  if(c<-.999999){
    let px=-ay,py=ax,pz=0;if(Math.abs(az)>.9){px=0;py=-az;pz=ay;}
    const l=Math.hypot(px,py,pz);px/=l;py/=l;pz/=l;
    out[o]=2*px*px-1;out[o+1]=2*px*py;out[o+2]=2*px*pz;out[o+3]=2*py*px;out[o+4]=2*py*py-1;out[o+5]=2*py*pz;out[o+6]=2*pz*px;out[o+7]=2*pz*py;out[o+8]=2*pz*pz-1;
    return out;
  }
  const k=1/(1+c);
  out[o]=c+vx*vx*k;out[o+1]=vx*vy*k-vz;out[o+2]=vx*vz*k+vy;
  out[o+3]=vy*vx*k+vz;out[o+4]=c+vy*vy*k;out[o+5]=vy*vz*k-vx;
  out[o+6]=vz*vx*k-vy;out[o+7]=vz*vy*k+vx;out[o+8]=c+vz*vz*k;
  return out;
}
const scratch=new Float64Array(9),parent=new Float64Array(9);
function multiply(a,b,out,o){
  for(let r=0;r<3;r++)for(let c=0;c<3;c++)scratch[r*3+c]=a[r*3]*b[c]+a[r*3+1]*b[3+c]+a[r*3+2]*b[6+c];
  for(let i=0;i<9;i++)out[o+i]=scratch[i];
}
const copyPoint=(b,out)=>{out[0]=b[0];out[1]=b[1];out[2]=b[2];return out;};

export class PeelPetals{
  // chains[k]: {rest, normal, side} (n*3 numbers, tip first), width (n half widths),
  // inner (mid-peel to its inner face) and depth (mid-peel to the fruit surface).
  constructor({chains,center,radii,shell,floor=0,scale=1,random=Math.random,bind=p=>[...p],sample=copyPoint,gravity=7,adhesion=.045,maxStrands=72}){
    this.n=chains[0].rest.length/3;this.center=[...center];this.radii=[...radii];this.shell=shell;this.floor=floor;this.scale=scale;
    this.random=random;this.bindPoint=bind;this.samplePoint=sample;this.gravity=gravity*scale;this.adhesion=adhesion*scale;this.maxStrands=maxStrands;
    const n=this.n;
    this.chains=chains.map(source=>{
      const rest=Float64Array.from(source.rest),segment=new Float64Array(n-1),bend=new Float64Array(n-2);
      for(let i=0;i<n-1;i++)segment[i]=Math.hypot(rest[i*3]-rest[i*3+3],rest[i*3+1]-rest[i*3+4],rest[i*3+2]-rest[i*3+5]);
      for(let i=0;i<n-2;i++)bend[i]=Math.hypot(rest[i*3]-rest[i*3+6],rest[i*3+1]-rest[i*3+7],rest[i*3+2]-rest[i*3+8]);
      return {rest,normal:Float64Array.from(source.normal),side:Float64Array.from(source.side),width:Float64Array.from(source.width),
        inner:source.inner,depth:source.depth,segment,bend,
        bindings:Array.from({length:n},(_,i)=>bind([rest[i*3],rest[i*3+1],rest[i*3+2]])),
        x:Float64Array.from(rest),prev:Float64Array.from(rest),home:Float64Array.from(rest),start:Float64Array.from(rest),
        rotation:new Float64Array((n-1)*9),front:0,tension:0};
    });
    this.centerBinding=bind([...center]);this.currentCenter=[...center];
    this.grabbed=null;this.closing=-1;this.strands=[];this.activity=0;this.quiet=1;this.detaches=0;this.snaps=0;
    this.onDetach=null;this.onSnap=null;this.onClosed=null;
    this.point=[0,0,0];this.attached=[0,0,0];
    for(const chain of this.chains)for(let s=0;s<n-1;s++)chain.rotation.set(identity,s*9);
  }
  get count(){return this.chains.length;}
  front(k){return this.chains[k].front;}
  peeled(k){return this.chains[k].front/(this.n-1);}
  get opened(){return Math.min(...this.chains.map((_,k)=>this.peeled(k)));}
  // Grab node i of petal k. A grab on the clinging part takes the peel's edge.
  grab(k,i,target){
    const chain=this.chains[k];if(!chain)return false;
    if(i>=chain.front){if(chain.front>=this.n-1)return false;i=chain.front;}
    this.grabbed={k,i,target:[...target]};this.closing=-1;this.quiet=0;return true;
  }
  move(target){if(this.grabbed&&target.every(Number.isFinite))this.grabbed.target=[...target];}
  release(){if(this.grabbed){this.grabbed=null;this.quiet=0;}}
  // Closing swings each free node back around its petal's base, like a
  // flower folding up at dusk, then the petals cling to the fruit again.
  close(){
    if(!this.chains.some(chain=>chain.front>0))return false;
    this.grabbed=null;this.closing=0;this.quiet=0;
    const b=(this.n-1)*3;
    for(const chain of this.chains){
      chain.from=new Float64Array(chain.front*3);
      for(let i=0;i<chain.front*3;i++)chain.from[i]=chain.x[i]-chain.home[b+i%3];
    }
    return true;
  }
  swingBack(){
    const t=Math.min(1,this.closing/.85),e=t*t*(3-2*t),b=(this.n-1)*3;
    for(const chain of this.chains){
      const h=chain.home,from=chain.from;if(!from)continue;
      for(let i=0;i<from.length/3;i++){
        const p=i*3,ax=from[p],ay=from[p+1],az=from[p+2],bx=h[p]-h[b],by=h[p+1]-h[b+1],bz=h[p+2]-h[b+2];
        const la=Math.hypot(ax,ay,az)||1e-9,lb=Math.hypot(bx,by,bz)||1e-9,c=Math.max(-1,Math.min(1,(ax*bx+ay*by+az*bz)/(la*lb)));
        const theta=Math.acos(c),s=Math.sin(theta),wa=s>1e-5?Math.sin((1-e)*theta)/s:1-e,wb=s>1e-5?Math.sin(e*theta)/s:e,l=la+(lb-la)*e;
        const dx=ax/la*wa+bx/lb*wb,dy=ay/la*wa+by/lb*wb,dz=az/la*wa+bz/lb*wb,dl=Math.hypot(dx,dy,dz)||1;
        chain.x[p]=h[b]+dx/dl*l;chain.x[p+1]=h[b+1]+dy/dl*l;chain.x[p+2]=h[b+2]+dz/dl*l;
        chain.prev[p]=chain.x[p];chain.prev[p+1]=chain.x[p+1];chain.prev[p+2]=chain.x[p+2];
      }
    }
    if(t<1)return;
    for(const chain of this.chains){chain.front=0;chain.from=null;chain.x.set(chain.home);chain.prev.set(chain.home);for(let s=0;s<this.n-1;s++)chain.rotation.set(identity,s*9);}
    this.strands.length=0;this.closing=-1;this.onClosed?.();
  }
  reset(){
    for(const chain of this.chains){chain.front=0;chain.tension=0;chain.x.set(chain.home);chain.prev.set(chain.home);for(let s=0;s<this.n-1;s++)chain.rotation.set(identity,s*9);}
    this.grabbed=null;this.closing=-1;this.strands.length=0;this.quiet=1;this.activity=0;
  }
  // A swing path for node i of petal k: from where it is now, around the
  // petal's base, out to the fully opened pose a little beyond its reach.
  // Screen drags follow this rail, so even the petal facing the viewer opens.
  rail(k,i){
    const chain=this.chains[k],n=this.n,front=chain.front;if(i>=front)i=front;
    const h=chain.home,b=(n-1)*3,H=[h[b],h[b+1],h[b+2]],P=i<front?chain.x:chain.home,S=[P[i*3],P[i*3+1],P[i*3+2]];
    let reach=0;for(let j=i;j<n-1;j++)reach+=chain.segment[j];
    const c=this.currentCenter;let ox=H[0]-c[0],oz=H[2]-c[2];const ol=Math.hypot(ox,oz)||1;ox/=ol;oz/=ol;
    const tilt=-.18,end=reach*1.1,E=[H[0]+ox*Math.cos(tilt)*end,H[1]+Math.sin(tilt)*end,H[2]+oz*Math.cos(tilt)*end];
    const a=S.map((v,j)=>v-H[j]),e=E.map((v,j)=>v-H[j]),la=Math.hypot(...a)||1e-9,lb=Math.hypot(...e);
    const ua=a.map(v=>v/la),ub=e.map(v=>v/lb);
    let axis=[ua[1]*ub[2]-ua[2]*ub[1],ua[2]*ub[0]-ua[0]*ub[2],ua[0]*ub[1]-ua[1]*ub[0]];const al=Math.hypot(...axis);
    if(al<1e-6){axis=[-oz,0,ox];}else axis=axis.map(v=>v/al);
    const angle=Math.acos(Math.max(-1,Math.min(1,ua[0]*ub[0]+ua[1]*ub[1]+ua[2]*ub[2])));
    return {k,i,hinge:H,start:S,direction:ua,axis,angle,from:la,to:lb};
  }
  railPoint(r,s,out=[0,0,0]){
    const t=Math.max(0,Math.min(1,s)),theta=r.angle*t,c=Math.cos(theta),sn=Math.sin(theta),a=r.axis,d=r.direction;
    const dot=a[0]*d[0]+a[1]*d[1]+a[2]*d[2],cross=[a[1]*d[2]-a[2]*d[1],a[2]*d[0]-a[0]*d[2],a[0]*d[1]-a[1]*d[0]];
    const radius=r.from+(r.to-r.from)*t;
    for(let j=0;j<3;j++)out[j]=r.hinge[j]+(d[j]*c+cross[j]*sn+a[j]*dot*(1-c))*radius;
    return out;
  }
  // Rest length of the free peel between node i and the hinge.
  reach(chain,i){let total=0;for(let j=i;j<chain.front;j++)total+=chain.segment[j];return total;}
  tryPeel(){
    const g=this.grabbed,chain=this.chains[g.k],front=chain.front;
    if(front>=this.n-1){chain.tension=0;return false;}
    const h=chain.home,k=front*3,t=g.target;
    const dx=t[0]-h[k],dy=t[1]-h[k+1],dz=t[2]-h[k+2],distance=Math.hypot(dx,dy,dz);
    const excess=distance-this.reach(chain,g.i);
    if(distance<1e-9||excess<=0){chain.tension=0;return false;}
    // Kendall: the work to open a strip grows as 1 - cos θ shrinks.
    let tx=h[k+3]-h[k],ty=h[k+4]-h[k+1],tz=h[k+5]-h[k+2];const tl=Math.hypot(tx,ty,tz)||1;tx/=tl;ty/=tl;tz/=tl;
    const cos=(dx*tx+dy*ty+dz*tz)/distance,outward=(dx*chain.normal[k]+dy*chain.normal[k+1]+dz*chain.normal[k+2])/distance;
    const drive=outward<-.3?0:excess*(1-cos)*.5;
    chain.tension=Math.min(1,drive/this.adhesion);
    if(drive<this.adhesion)return false;
    this.detach(g.k);return true;
  }
  detach(k){
    const chain=this.chains[k],i=chain.front,p=i*3;chain.front++;
    for(let a=0;a<3;a++){chain.x[p+a]=chain.home[p+a];chain.prev[p+a]=chain.home[p+a];}
    this.spawnStrands(k,i);this.detaches++;this.quiet=0;this.onDetach?.(k,i);
  }
  spawnStrands(k,i){
    if(i===0)return;
    const chain=this.chains[k],L=this.scale,p=i*3;
    let count=(this.random()<.8?1:0)+(this.random()<.4?1:0);
    for(;count>0;count--){
      if(this.strands.length>=this.maxStrands){const old=this.strands.findIndex(s=>s.state==='wisp');this.strands.splice(old<0?0:old,1);}
      const lateral=(this.random()*2-1)*.65*chain.width[i],point=depth=>[0,1,2].map(a=>chain.rest[p+a]+chain.side[p+a]*lateral-chain.normal[p+a]*depth);
      const petal=point(chain.inner),anchor=point(chain.depth);
      this.strands.push({k,i,petal,petalBinding:this.bindPoint(petal),anchor:this.bindPoint(anchor),
        maxLength:(.10+.22*this.random())*L,radius:(.008+.005*this.random())*L,wisp:(.07+.10*this.random())*L,
        state:'stretch',age:0,length:0,snapLength:0,a:[0,0,0],b:[0,0,0],dir:[0,1,0],snapDir:[0,1,0],normal:[0,1,0]});
    }
  }
  removeStrands(k,i){this.strands=this.strands.filter(s=>s.k!==k||s.i<i);}
  pivot(chain,i){return i<chain.front?chain.x:chain.home;}
  // Keep a free node outside the fruit and above the floor.
  collide(chain,i){
    const x=chain.x,p=i*3,c=this.currentCenter,R=this.radii;
    const qx=(x[p]-c[0])/R[0],qy=(x[p+1]-c[1])/R[1],qz=(x[p+2]-c[2])/R[2],d=Math.hypot(qx,qy,qz);
    if(d<this.shell&&d>1e-6){const s=this.shell/d;x[p]=c[0]+qx*s*R[0];x[p+1]=c[1]+qy*s*R[1];x[p+2]=c[2]+qz*s*R[2];}
    const floor=this.floor+.012*this.scale;
    if(x[p+1]<floor){
      x[p+1]=floor;
      // Floor friction: lose most of the sliding speed on contact.
      chain.prev[p]+=(x[p]-chain.prev[p])*.45;chain.prev[p+2]+=(x[p+2]-chain.prev[p+2])*.45;
      if(chain.prev[p+1]<floor)chain.prev[p+1]=floor;
    }
  }
  solve(k,grabbedNode){
    const chain=this.chains[k],front=chain.front,x=chain.x;
    const free=i=>i<front&&i!==grabbedNode?1:0;
    for(let j=0;j<front;j++){
      const wa=free(j),wb=free(j+1),w=wa+wb;if(!w)continue;
      const a=j*3,b=a+3,dx=x[b]-x[a],dy=x[b+1]-x[a+1],dz=x[b+2]-x[a+2],d=Math.hypot(dx,dy,dz);
      if(d<1e-9)continue;const f=(d-chain.segment[j])/(d*w);
      x[a]+=dx*f*wa;x[a+1]+=dy*f*wa;x[a+2]+=dz*f*wa;x[b]-=dx*f*wb;x[b+1]-=dy*f*wb;x[b+2]-=dz*f*wb;
    }
    // Peel keeps its curl: soft constraints across each pair of segments, but not across the hinge.
    for(let j=0;j+2<front;j++){
      const wa=free(j),wb=free(j+2),w=wa+wb;if(!w)continue;
      const a=j*3,b=a+6,dx=x[b]-x[a],dy=x[b+1]-x[a+1],dz=x[b+2]-x[a+2],d=Math.hypot(dx,dy,dz);
      if(d<1e-9)continue;const f=.8*(d-chain.bend[j])/(d*w);
      x[a]+=dx*f*wa;x[a+1]+=dy*f*wa;x[a+2]+=dz*f*wa;x[b]-=dx*f*wb;x[b+1]-=dy*f*wb;x[b+2]-=dz*f*wb;
    }
    for(let i=0;i<front;i++)if(i!==grabbedNode)this.collide(chain,i);
  }
  // Where the grabbed node may go: within its free length of the hinge, outside the fruit.
  grabTarget(chain,i,target,out){
    const h=chain.home,k=chain.front*3,reach=this.reach(chain,i);
    let dx=target[0]-h[k],dy=target[1]-h[k+1],dz=target[2]-h[k+2];const d=Math.hypot(dx,dy,dz);
    if(d>reach&&d>1e-9){dx*=reach/d;dy*=reach/d;dz*=reach/d;}
    out[0]=h[k]+dx;out[1]=h[k+1]+dy;out[2]=h[k+2]+dz;
    const c=this.currentCenter,R=this.radii,qx=(out[0]-c[0])/R[0],qy=(out[1]-c[1])/R[1],qz=(out[2]-c[2])/R[2],q=Math.hypot(qx,qy,qz);
    if(q<this.shell&&q>1e-6){const s=this.shell/q;out[0]=c[0]+qx*s*R[0];out[1]=c[1]+qy*s*R[1];out[2]=c[2]+qz*s*R[2];}
    return out;
  }
  step(dt){
    if(!(dt>0))return;
    dt=Math.min(dt,1/30);
    const n=this.n,L=this.scale,point=this.point;
    // Clinging nodes ride on the fruit wherever the soft body carries them.
    this.samplePoint(this.centerBinding,this.currentCenter);
    let follow=0;
    for(const chain of this.chains){
      chain.start.set(chain.x);
      for(let i=0;i<n;i++){
        const p=i*3;this.samplePoint(chain.bindings[i],point);
        follow=Math.max(follow,Math.abs(point[0]-chain.home[p])+Math.abs(point[1]-chain.home[p+1])+Math.abs(point[2]-chain.home[p+2]));
        chain.home[p]=point[0];chain.home[p+1]=point[1];chain.home[p+2]=point[2];
        if(i>=chain.front){chain.x[p]=point[0];chain.x[p+1]=point[1];chain.x[p+2]=point[2];chain.prev[p]=point[0];chain.prev[p+1]=point[1];chain.prev[p+2]=point[2];}
      }
    }
    if(this.grabbed){for(let pass=0;pass<2&&this.tryPeel();pass++);}
    else for(const chain of this.chains)chain.tension=0;
    if(this.closing>=0){
      this.closing+=dt;this.swingBack();
      if(this.closing>=0){this.updateRotations();this.activity=1;this.quiet=0;this.updateStrands(dt);return;}
      this.activity=1;this.quiet=0;this.updateStrands(dt);return;
    }
    const anyFree=this.chains.some(chain=>chain.front>0);
    const snapping=this.strands.some(s=>s.state==='snap');
    if(!anyFree&&!snapping){this.activity=follow>1e-7?1:0;this.updateStrands(dt);return;}
    if(this.quiet>=.5&&!this.grabbed&&this.closing<0&&follow<1e-6*L&&!snapping){this.activity=0;return;}
    const substeps=4,h=dt/substeps,damping=Math.exp(-1.6*h),fall=this.gravity*h*h,goal=[0,0,0];
    for(let s=0;s<substeps;s++){
      for(let k=0;k<this.chains.length;k++){
        const chain=this.chains[k],front=chain.front;if(!front)continue;
        const grabbedNode=this.grabbed?.k===k&&this.grabbed.i<front?this.grabbed.i:-1;
        for(let i=0;i<front;i++){
          const p=i*3,x=chain.x,prev=chain.prev;
          if(i===grabbedNode){
            this.grabTarget(chain,i,this.grabbed.target,goal);
            for(let a=0;a<3;a++){prev[p+a]=x[p+a];x[p+a]=goal[a];}
            continue;
          }
          for(let a=0;a<3;a++){const v=(x[p+a]-prev[p+a])*damping;prev[p+a]=x[p+a];x[p+a]+=v;}
          x[p+1]-=fall;
        }
      }
      for(let iteration=0;iteration<6;iteration++){
        for(let k=0;k<this.chains.length;k++){
          const chain=this.chains[k],grabbedNode=this.grabbed?.k===k&&this.grabbed.i<chain.front?this.grabbed.i:-1;
          if(chain.front)this.solve(k,grabbedNode);
        }
      }
    }
    this.updateRotations();
    let motion=0;
    for(const chain of this.chains)for(let i=0;i<chain.front;i++){const p=i*3;motion=Math.max(motion,Math.abs(chain.x[p]-chain.start[p])+Math.abs(chain.x[p+1]-chain.start[p+1])+Math.abs(chain.x[p+2]-chain.start[p+2]));}
    const moving=!!this.grabbed||this.closing>=0||motion>3e-4*L||follow>1e-6*L;
    this.quiet=moving?0:this.quiet+dt;
    this.activity=this.quiet<.5||snapping?1:0;
    this.updateStrands(dt);
  }
  // Parallel-transport frames from the hinge outward: the least twisting rotation per segment.
  updateRotations(){
    for(const chain of this.chains){
      const front=chain.front,r=chain.rest,x=chain.x,M=chain.rotation;
      for(let s=front-1;s>=0;s--){
        const a=s*3,b=a+3,P=this.pivot(chain,s+1);
        let rx=r[a]-r[b],ry=r[a+1]-r[b+1],rz=r[a+2]-r[b+2];const rl=Math.hypot(rx,ry,rz)||1;rx/=rl;ry/=rl;rz/=rl;
        if(s<front-1){
          parent.set(M.subarray((s+1)*9,(s+2)*9));
          const qx=parent[0]*rx+parent[1]*ry+parent[2]*rz,qy=parent[3]*rx+parent[4]*ry+parent[5]*rz,qz=parent[6]*rx+parent[7]*ry+parent[8]*rz;
          rx=qx;ry=qy;rz=qz;
        }else parent.set(identity);
        let cx=x[a]-P[b],cy=x[a+1]-P[b+1],cz=x[a+2]-P[b+2];const cl=Math.hypot(cx,cy,cz)||1;cx/=cl;cy/=cl;cz/=cl;
        rotationBetween(rx,ry,rz,cx,cy,cz,M,s*9);
        multiply(M.subarray(s*9,s*9+9),parent,M,s*9);
      }
    }
  }
  // Skin a rest point of petal k at fractional node index f. `attached` is where
  // the clinging peel would put it (the fruit's own deformation).
  skin(k,px,py,pz,f,out,attached){
    const chain=this.chains[k],front=chain.front,n=this.n;
    let j=Math.floor(f);if(j>n-2)j=n-2;if(j<0)j=0;
    const u=f-j,wPrev=j>0?Math.max(0,.5-u):0,wNext=j<n-2?Math.max(0,u-.5):0;
    if(front===0||(wPrev>0?j-1:j)>=front){out[0]=attached[0];out[1]=attached[1];out[2]=attached[2];return out;}
    out[0]=0;out[1]=0;out[2]=0;
    this.addSegment(chain,j,1-wPrev-wNext,px,py,pz,out,attached);
    if(wPrev)this.addSegment(chain,j-1,wPrev,px,py,pz,out,attached);
    if(wNext)this.addSegment(chain,j+1,wNext,px,py,pz,out,attached);
    return out;
  }
  addSegment(chain,s,w,px,py,pz,out,attached){
    if(s>=chain.front){out[0]+=attached[0]*w;out[1]+=attached[1]*w;out[2]+=attached[2]*w;return;}
    const b=(s+1)*3,P=this.pivot(chain,s+1),r=chain.rest,M=chain.rotation,m=s*9;
    const vx=px-r[b],vy=py-r[b+1],vz=pz-r[b+2];
    out[0]+=w*(P[b]+M[m]*vx+M[m+1]*vy+M[m+2]*vz);
    out[1]+=w*(P[b+1]+M[m+3]*vx+M[m+4]*vy+M[m+5]*vz);
    out[2]+=w*(P[b+2]+M[m+6]*vx+M[m+7]*vy+M[m+8]*vz);
  }
  updateStrands(dt){
    const c=this.currentCenter,R=this.radii,attached=this.attached;
    for(const s of this.strands){
      this.samplePoint(s.anchor,s.a);
      const nx=(s.a[0]-c[0])/(R[0]*R[0]),ny=(s.a[1]-c[1])/(R[1]*R[1]),nz=(s.a[2]-c[2])/(R[2]*R[2]),nl=Math.hypot(nx,ny,nz)||1;
      s.normal[0]=nx/nl;s.normal[1]=ny/nl;s.normal[2]=nz/nl;
      if(s.state==='stretch'){
        this.samplePoint(s.petalBinding,attached);
        this.skin(s.k,s.petal[0],s.petal[1],s.petal[2],s.i,s.b,attached);
        const dx=s.b[0]-s.a[0],dy=s.b[1]-s.a[1],dz=s.b[2]-s.a[2],length=Math.hypot(dx,dy,dz);
        s.length=length;if(length>1e-9){s.dir[0]=dx/length;s.dir[1]=dy/length;s.dir[2]=dz/length;}
        if(length>s.maxLength){s.state='snap';s.age=0;s.snapLength=length;s.snapDir=[...s.dir];this.snaps++;this.onSnap?.(s);}
        continue;
      }
      // A snapped strand springs back and lies along the fruit as a short wisp.
      const n=s.normal,d=s.snapDir,along=d[0]*n[0]+d[1]*n[1]+d[2]*n[2];
      let tx=d[0]-n[0]*along,ty=d[1]-n[1]*along,tz=d[2]-n[2]*along;const tl=Math.hypot(tx,ty,tz);
      if(tl<1e-6){tx=0;ty=1;tz=0;}else{tx/=tl;ty/=tl;tz/=tl;}
      if(s.state==='snap'){s.age+=dt;if(s.age>=.22)s.state='wisp';}
      const e=s.state==='wisp'?1:1-(1-Math.min(1,s.age/.22))**3;
      const x=d[0]+(tx-d[0])*e,y=d[1]+(ty-d[1])*e,z=d[2]+(tz-d[2])*e,l=Math.hypot(x,y,z)||1;
      s.dir[0]=x/l;s.dir[1]=y/l;s.dir[2]=z/l;s.length=s.snapLength+(s.wisp-s.snapLength)*e;
    }
  }
}
